import { Icon } from '@iconify/react';
import { type ReactNode } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import PencilIcon from 'src/assets/icons/PencilIcon';
import CustomTitleBar from 'src/components/GuideMeTitleBar';
import Button from 'src/components/design-system/Button';

/** Ignores Trans-injected children so the icon always renders. */
const GuidePencilIcon = (_props: { children?: ReactNode }) => (
  <span className="inline-flex h-7 w-7 items-center justify-center rounded border border-[#D1D1D1] bg-white align-middle text-info">
    <PencilIcon width={14} height={14} />
  </span>
);

/** Ignores Trans-injected children so the icon always renders. */
const GuideMoreOptionsIcon = (_props: { children?: ReactNode }) => (
  <span className="inline-flex h-7 w-7 items-center justify-center rounded border border-[#D1D1D1] bg-[#F5F5F5] align-middle text-[#4A5565]">
    <Icon icon="lucide:ellipsis-vertical" height={14} width={14} />
  </span>
);

const ShoppingListsContent = () => {
  const { t } = useTranslation();

  return (
    <ul className="space-y-1 px-4 font-bold text-[13px]">
      <li className="list-disc font-semibold">{t('shoppingListsGuide.displaysCards')}</li>
      <li className="list-disc font-semibold">
        <Trans
          i18nKey="shoppingListsGuide.viewProducts"
          components={{
            viewProducts: (
              <Button color="info" size="md" className="inline-block" readonly>
                {null}
              </Button>
            ),
          }}
        />
      </li>
      <li className="list-disc font-semibold">
        <Trans
          i18nKey="shoppingListsGuide.createNewList"
          components={{
            createButton: (
              <Button color="primary" size="md" className="inline-block" readonly>
                {null}
              </Button>
            ),
          }}
        />
      </li>
      <li className="list-disc font-semibold">{t('shoppingListsGuide.selectCheckbox')}</li>
      <li className="list-disc font-semibold">
        <Trans
          i18nKey="shoppingListsGuide.editPencil"
          components={{
            pencil: <GuidePencilIcon />,
          }}
        />
      </li>
      <li className="list-disc font-semibold">
        <Trans
          i18nKey="shoppingListsGuide.moreOptions"
          components={{
            moreOptions: <GuideMoreOptionsIcon />,
          }}
        />
      </li>
      <li className="list-disc font-semibold">
        <Trans
          i18nKey="shoppingListsGuide.sharedByOwner"
          components={{
            sharedTag: (
              <span className="inline-flex items-center rounded-full bg-[#3FA9DB33] px-3 py-1.5 text-[11px] font-normal leading-tight text-[rgba(0,0,0,1)] align-middle" />
            ),
          }}
        />
      </li>
      <li className="list-disc font-semibold">
        <Trans
          i18nKey="shoppingListsGuide.share"
          components={{
            shareButton: (
              <Button color="info" size="md" className="inline-block" readonly>
                {null}
              </Button>
            ),
          }}
        />
      </li>
      <li className="list-disc font-semibold">
        <Trans
          i18nKey="shoppingListsGuide.sendACopy"
          components={{
            copyButton: (
              <Button color="info" size="md" className="inline-block" readonly>
                {null}
              </Button>
            ),
          }}
        />
      </li>
      <li className="list-disc font-semibold">
        <Trans
          i18nKey="shoppingListsGuide.delete"
          components={{
            deleteButton: (
              <Button color="error" size="md" className="inline-block" readonly>
                {null}
              </Button>
            ),
          }}
        />
      </li>
    </ul>
  );
};

export const useGuideMeSteps = () => {
  const { t } = useTranslation();

  return [
    {
      content: <ShoppingListsContent />,
      title: (
        <CustomTitleBar
          title={t('shoppingListsGuide.title')}
          className="font-bold text-xl text-red-600"
        />
      ),
    },
  ];
};

export default useGuideMeSteps;
